import { useMutation, useQuery } from "@tanstack/react-query";
import { FileSpreadsheet } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/toast";
import { api, errorMessage, unwrap } from "@/lib/api";
import { useT } from "@/lib/i18n";

type Kind = "products" | "leads" | "sales";

/** Jadvalni Excel'ga eksport: fonda tayyorlanadi, tayyor bo'lgach avtomatik yuklab olinadi. */
export function ExportButton({ kind, params }: { kind: Kind; params?: Record<string, string> }) {
  const { t } = useT();
  const toast = useToast();
  const [jobId, setJobId] = useState<string | null>(null);

  const start = useMutation({
    mutationFn: async () =>
      unwrap(await api.POST("/api/exports/", { body: { kind, params: params ?? {} } })),
    onSuccess: (job) => setJobId(job.id),
    onError: (e) => toast.error(errorMessage(e, t("common.error"))),
  });

  useQuery({
    queryKey: ["export-job", jobId],
    enabled: !!jobId,
    queryFn: async () => {
      const job = unwrap(await api.GET("/api/exports/{id}/", { params: { path: { id: jobId! } } }));
      if (job.status === "ready" && job.download_url) {
        const a = document.createElement("a");
        a.href = job.download_url;
        a.download = `${kind}.xlsx`;
        a.click();
        setJobId(null);
      } else if (job.status === "failed") {
        toast.error(job.error || t("common.error"));
        setJobId(null);
      }
      return job;
    },
    refetchInterval: 1500,
  });

  const busy = start.isPending || !!jobId;
  return (
    <Button variant="outline" disabled={busy} onClick={() => start.mutate()}>
      <FileSpreadsheet className="size-4" /> {busy ? t("export.preparing") : t("export.excel")}
    </Button>
  );
}
